import { FC, ReactNode, useContext } from 'react';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';

// components
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { PdpSharedDeposit } from '../PdpSharedDeposit/PdpSharedDeposit';
import { addPageAction } from '../../utils/tracking/rumTracking';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FollowSearch } from '../pdpMessages';

// helpers
import classNames from 'classnames';
import { getDisplayInfo } from '../helpers/getDisplayInfo';
import {
    locations,
    trackFollowSearchAdded,
    trackFollowSearchRemoved,
} from '../../utils/tracking/shared/followSearchTracking';

// styles
import styles from './PdpRespSidebarButtons.scss';

import { PdpRespSidebarButtons_user$data } from './__generated__/PdpRespSidebarButtons_user.graphql';
import { PdpRespSidebarButtons_item$data } from './__generated__/PdpRespSidebarButtons_item.graphql';
import { PdpRespSidebarButtons_itemSearch$data } from './__generated__/PdpRespSidebarButtons_itemSearch.graphql';
import { PdpRespSidebarButtons_displayInfo$data } from './__generated__/PdpRespSidebarButtons_displayInfo.graphql';

type Props = {
    user: PdpRespSidebarButtons_user$data | null;
    item: PdpRespSidebarButtons_item$data;
    itemSearch: PdpRespSidebarButtons_itemSearch$data | null;
    displayInfo: PdpRespSidebarButtons_displayInfo$data | null;
    relayVariables: { itemId: string; userId?: string | null; isMultiSku?: boolean };
    isClient?: boolean;
    relay: RelayProp;
    quantitySelected?: string;
    currency?: string;
    skuId?: string;
};

// messages
const followSearchText = (searchName: string): ReactNode => (
    <p className={styles.followSearchText}>
        <FollowSearch searchName={searchName} searchNameClassname={styles.searchName} />
    </p>
);

const PdpRespSidebarButtonsComponent: FC<Props> = props => {
    const { relayVariables, user, item, quantitySelected, skuId, itemSearch } = props;
    const { itemId, userId, isMultiSku } = relayVariables;

    const { isPdpDesktopSiderailRedesign: isRedesignVariant } = useContext(ServerVarsContext);

    const displayInfo = getDisplayInfo({
        displayInfo: props.displayInfo,
        skus: item.skus,
        selectedSkuId: skuId,
        isMultiSku,
    });

    const { isSold: isSoldPdp, isUnpurchasable, saveSearchData } = item || {};
    const { isVerifiedTrade, favorites } = user || {};

    if (!displayInfo?.ctas || isSoldPdp || isUnpurchasable) {
        return null;
    }

    const { showPurchase, showMakeOffer, showRequestPrice, showFollowCategory, showAcceptOffer } =
        displayInfo.ctas;
    const buttonsClass = classNames({ [styles.hasQuantitySelect]: quantitySelected });
    const showDyoVersion = item?.isDyoCustomizable && showPurchase;

    const PurchaseButton: FC = () =>
        showPurchase ? (
            <PdpSharedPurchaseButton
                itemId={itemId}
                item={item}
                userId={userId}
                quantitySelected={quantitySelected}
                skuId={skuId}
                isPrimary={!showDyoVersion}
            />
        ) : null;

    const MakeOfferButton: FC = () =>
        showMakeOffer ? (
            <CurrencyProvider
                render={({ currency }: { currency: string }) => (
                    <PdpSharedMakeOfferButton
                        itemId={itemId}
                        selectedSkuId={skuId}
                        item={item}
                        userId={userId}
                        user={user}
                        quantitySelected={quantitySelected}
                        currency={currency}
                        hasPrivateOffer={showAcceptOffer}
                        ctaCopy={
                            isRedesignVariant ? (
                                <FormattedMessage
                                    id="pdp.PdpRespSidebarButtons.makeOfferCta"
                                    defaultMessage="Make Offer"
                                />
                            ) : undefined
                        }
                        showArrow={!isRedesignVariant && !showDyoVersion}
                        gaLabel="side rail cta"
                    />
                )}
            />
        ) : null;

    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    addPageAction('respPdpCTAsRendered', { itemId });

    const hasInlineButtons = // Secondary CTA buttons are inlined
        showDyoVersion || // if "Design Your Own" button is present
        isRedesignVariant; // and in all cases in TOF-445 redesign

    const has2SecondaryCtas = showPurchase && showMakeOffer;

    // secondary CTAs are the buttons below the title
    let secondaryCtas: ReactNode;

    if (showRequestPrice) {
        // Request price CTA is exclusive to the rest, so display it alone if showRequestPrice
        secondaryCtas = (
            <PdpSharedContactDealerButton
                type={'REQUEST_PRICE'}
                buttonType={showDyoVersion ? 'secondary' : 'primary'}
                item={item}
                selectedSkuId={skuId}
                user={user}
            />
        );
    } else if (hasInlineButtons && has2SecondaryCtas) {
        // If there are two secondary CTAs and buttons are displayed inline, put buttons in required wrappers
        const inlineCtaClass = classNames(styles.inlineCta, {
            [styles.evenColumns]: isRedesignVariant,
        });

        const [firstCta, secondCta] = isRedesignVariant
            ? [<MakeOfferButton />, <PurchaseButton />]
            : [<PurchaseButton />, <MakeOfferButton />];

        secondaryCtas = (
            <div className={styles.inlineCtaContainer}>
                <span className={inlineCtaClass}>{firstCta}</span>
                <span className={inlineCtaClass}>{secondCta}</span>
            </div>
        );
    } else {
        // Otherwise just display buttons as is
        secondaryCtas = (
            <>
                <PurchaseButton />
                <MakeOfferButton />
            </>
        );
    }

    return (
        <div className={buttonsClass} data-tn="item-actions">
            {/* Design Your Own */}
            {showDyoVersion && (
                <div className={styles.dyoCtaContainer}>
                    <PdpSharedContactDealerButton
                        type={'DESIGN_YOUR_OWN'}
                        item={item}
                        selectedSkuId={skuId}
                        launchOrigin="dyo main cta"
                        user={user}
                    />
                </div>
            )}

            {secondaryCtas}

            <PdpSharedDeposit userId={userId} item={item} />

            {/* Follow Search */}
            {showFollowCategory && followSearchName && isVerifiedTrade ? (
                // trade users
                <div className={styles.followTrade}>
                    {followSearchText(followSearchName)}
                    <div>
                        <SaveSearchButton
                            size="small"
                            className={styles.followButtonTrade}
                            userId={userId}
                            favoritesConnection={favorites}
                            page={followSearchPage}
                            name={followSearchName}
                            location={isSoldPdp && locations.SOLD_PDP}
                            onFollowSearchCompleted={(isFollowing: boolean): void =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                            itemSearch={itemSearch}
                        />
                    </div>
                </div>
            ) : (
                showFollowCategory &&
                followSearchName && (
                    // non trade users
                    <div>
                        {followSearchText(followSearchName)}
                        <SaveSearchButton
                            userId={userId}
                            user={user}
                            favoritesConnection={favorites || null}
                            page={followSearchPage}
                            name={followSearchName}
                            fullWidth
                            location={isSoldPdp && locations.SOLD_PDP}
                            onFollowSearchCompleted={(isFollowing: boolean): void =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                            itemSearch={itemSearch}
                        />
                    </div>
                )
            )}
        </div>
    );
};

export const PdpRespSidebarButtons = createFragmentContainer(PdpRespSidebarButtonsComponent, {
    user: graphql`
        fragment PdpRespSidebarButtons_user on User {
            isVerifiedTrade
            ...PdpSharedContactDealerButton_user
            ...PdpSharedMakeOfferButton_user
            favorites(first: 1, types: [SEARCH, USER_PREFERENCE], pages: $followSearchPages)
                @include(if: $fetchFollowSearch) {
                ...SaveSearchButton_favoritesConnection
            }
        }
    `,
    item: graphql`
        fragment PdpRespSidebarButtons_item on Item {
            isDyoCustomizable
            isSold
            isUnpurchasable
            isInProcurement(userId: $userId) @include(if: $fetchUser)
            saveSearchData {
                name
                page
            }
            ...PdpSharedContactDealerButton_item
            ...PdpSharedPurchaseButton_item
            ...PdpSharedMakeOfferButton_item
            ...PdpSharedDeposit_item
            skus @include(if: $isMultiSku) {
                serviceId
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showPurchase
                        showMakeOffer
                        showRequestPrice
                        showFollowCategory
                        showAcceptOffer
                    }
                }
            }
        }
    `,
    itemSearch: graphql`
        fragment PdpRespSidebarButtons_itemSearch on ItemSearchQueryConnection {
            ...SaveSearchButton_itemSearch
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarButtons_displayInfo on ItemDisplayInfo {
            ctas {
                showPurchase
                showMakeOffer
                showRequestPrice
                showFollowCategory
                showAcceptOffer
            }
        }
    `,
});
